import * as React from "react";
import * as microsoftTeams from "@microsoft/teams-js";
import { Button, Menu, MenuTrigger, MenuPopover, MenuList, MenuItem, Tooltip } from "@fluentui/react-components";
import { ChevronDown12Regular } from "@fluentui/react-icons";
import StringsStore from "sh-strings/store";
import { IMsTeamWithScheduleInfo, IRestorableScheduleTeam } from "sh-models";
import { TeamSetupMode } from "../store/schema/TeamSetupPickerStore";
import { ECSConfigKey, ECSConfigService, InstrumentationService } from "sh-services";
import { getGenericEventPropertiesObject } from "sh-instrumentation";
import PastSchedulesDialog from "./PastSchedulesDialog";
import { UserStore } from "sh-stores";
import { observer } from "mobx-react";

const styles = require("./TeamListItem.scss");

// Icons from local assets for dropdown menu items
const newScheduleIcon = require("../../../../../../assets/images/new-schedule-icon.svg");
const restorePastScheduleIcon = require("../../../../../../assets/images/restore-past-schedule-icon.svg");

export interface TeamListItemProps {
    teamWithScheduleInfo: IMsTeamWithScheduleInfo;
    isDisabled: boolean;
    onClick: (teamWithScheduleInfo: IMsTeamWithScheduleInfo, setupMode: TeamSetupMode) => void;
    isFirstItem?: boolean;
    buttonRef?: React.RefObject<HTMLButtonElement>;
}

interface TeamListItemState {
    isHovered: boolean;
    showPastSchedulesDialog: boolean;
}

/**
 * Individual team card component for the Create/Restore Schedule dialog
 */
@observer
export default class TeamListItem extends React.Component<TeamListItemProps, TeamListItemState> {
    private _strings: Map<string, string>;

    constructor(props: TeamListItemProps) {
        super(props);
        this._strings = StringsStore().registeredStringModules.get("msTeamPicker").strings;
        this.state = {
            isHovered: false,
            showPastSchedulesDialog: false
        };
    }

    private handleMouseEnter = () => {
        this.setState({ isHovered: true });
    }

    private handleMouseLeave = () => {
        this.setState({ isHovered: false });
    }

    /**
     * Get team metadata items for rendering
     * Format from Figma: "Public | 42 people | 3 channels | Description of Team goes here and truncates"
     *
     * Note: The Teams SDK's TeamInformation interface doesn't provide member count or channel count
     * directly from getMSTeamsUserJoinedTeams(). To display these counts, you would need to:
     * 1. Make additional Microsoft Graph API calls to fetch team members/channels
     * 2. Extend the IMsTeamWithScheduleInfo interface to include these counts
     * 3. Fetch this data in TeamSetupMSTeamPicker.loadInitialData() before rendering
     *
     * For now, we use placeholder values to match the Figma design.
     */
    private getTeamMetadataItems(teamInfo: microsoftTeams.TeamInformation): string[] {
        const metadata: string[] = [];

        // Add description if available
        if (teamInfo.teamDescription) {
            metadata.push(teamInfo.teamDescription);
        }

        return metadata;
    }

    /**
     * Get the aria label for the action button
     */
    private getButtonAriaLabel(teamInfo: microsoftTeams.TeamInformation, isRestore: boolean): string {
        if (isRestore) {
            return this._strings.get("restoreTeamScheduleButtonAriaLabel").format(teamInfo.teamName);
        }
        return this._strings.get("createTeamScheduleButtonAriaLabel").format(teamInfo.teamName);
    }

    /**
     * Get the button text
     */
    private getButtonText(isRestore: boolean): string {
        if (isRestore) {
            return this._strings.get("restoreScheduleButtonLabel");
        }
        return this._strings.get("selectTeamButtonLabel");
    }

    /**
     * Get restorable schedules for this team's group
     */
    private getRestorableSchedulesForGroup(): IRestorableScheduleTeam[] {
        const { teamWithScheduleInfo } = this.props;
        const groupId = teamWithScheduleInfo.msTeamInfo.groupId;

        if (!groupId) {
            return [];
        }

        const restorableSchedulesByGroup = UserStore().restorableSchedulesByGroup;
        if (!restorableSchedulesByGroup || restorableSchedulesByGroup.length === 0) {
            return [];
        }

        const groupEntry = restorableSchedulesByGroup.find(group => group.groupId === groupId);
        return groupEntry?.teams || [];
    }

    /**
     * Check if the team's group has restorable schedules
     */
    private hasRestorableSchedulesForGroup(): boolean {
        return this.getRestorableSchedulesForGroup().length > 0;
    }

    /**
     * Handle "New schedule" menu item click
     */
    private handleNewScheduleClick = () => {
        const { teamWithScheduleInfo, isDisabled } = this.props;
        if (!isDisabled) {
            this.props.onClick(teamWithScheduleInfo, TeamSetupMode.CreateNewSchedule);
        }
    }

    /**
     * Handle "Restore past schedule" menu item click
     */
    private handleRestorePastScheduleClick = () => {
        const { isDisabled } = this.props;
        if (!isDisabled) {
            InstrumentationService.logEvent(
                InstrumentationService.events.ProvisionScheduleTeam,
                [
                    getGenericEventPropertiesObject(InstrumentationService.properties.EventType, InstrumentationService.values.RestorePastScheduleMenuClicked),
                    getGenericEventPropertiesObject(InstrumentationService.properties.IsRestoreOperation, true)
                ]);
            // Open the Past Schedules dialog
            this.setState({ showPastSchedulesDialog: true });
        }
    }

    /**
     * Close the Past Schedules dialog
     */
    private handleClosePastSchedulesDialog = () => {
        this.setState({ showPastSchedulesDialog: false });
    }

    /**
     * Handle restore of a specific schedule
     */
    private handleRestoreSchedule = (scheduleId: string) => {
        // No-op: Keep the dialog open (showing "restoring" state) until app reloads.
        // Closing it would briefly expose the underlying TeamSetupCreateRestoreSchedule step.
    }

    /**
     * Render the team avatar placeholder
     * In a real implementation, this would show the team's avatar image
     */
    private renderTeamAvatar(teamInfo: microsoftTeams.TeamInformation): JSX.Element {
        // For now, render a placeholder with the first letter of the team name
        const initial = teamInfo.teamName ? teamInfo.teamName.charAt(0).toUpperCase() : "T";

        return (
            <div className={styles.teamAvatar} aria-hidden="true">
                <span className={styles.teamInitial}>{initial}</span>
            </div>
        );
    }

    render() {
        const { teamWithScheduleInfo, isDisabled, isFirstItem, buttonRef } = this.props;
        const teamInfo = teamWithScheduleInfo.msTeamInfo;
        const scheduleInfo = teamWithScheduleInfo.scheduleTeamInfo;

        // Check if this is a restore scenario
        const isRestoreScheduleEnabled = ECSConfigService.isECSFeatureEnabled(ECSConfigKey.RestoreScheduleEnabled);
        const hasRestorableSchedule = isRestoreScheduleEnabled && scheduleInfo && scheduleInfo.isSoftDeleted === true;

        // Get button properties
        const buttonText = this.getButtonText(hasRestorableSchedule);
        const buttonAriaLabel = this.getButtonAriaLabel(teamInfo, hasRestorableSchedule);
        const disabledTooltip = this._strings.get("disabledScheduleButtonTooltip");

        // Get team metadata items
        const metadataItems = this.getTeamMetadataItems(teamInfo);
        const metadataString = metadataItems.join(" | ");

        // Create aria-description for accessibility
        const ariaDescription = `${metadataString}. ${isDisabled ? disabledTooltip : buttonAriaLabel}`;

        // Create menu button with dropdown (only show when hovered on the row)
        const actionButton = this.state.isHovered && !isDisabled ? (
            <Menu>
                <MenuTrigger disableButtonEnhancement>
                    <Button
                        ref={isFirstItem ? buttonRef : undefined}
                        appearance="outline"
                        icon={<ChevronDown12Regular />}
                        iconPosition="after"
                        disabled={isDisabled}
                        aria-label="Create schedule options"
                        data-automation-id={`createButton_${teamInfo.teamName}`}
                        className={styles.menuButton}
                    >
                        Create
                    </Button>
                </MenuTrigger>
                <MenuPopover>
                    <MenuList>
                        <MenuItem
                            icon={<img src={newScheduleIcon} alt="" className={styles.menuIcon} />}
                            onClick={this.handleNewScheduleClick}
                        >
                            New schedule
                        </MenuItem>
                        <MenuItem
                            icon={<img src={restorePastScheduleIcon} alt="" className={styles.menuIcon} />}
                            onClick={this.handleRestorePastScheduleClick}
                            disabled={!this.hasRestorableSchedulesForGroup()}
                        >
                            Restore past schedule
                        </MenuItem>
                    </MenuList>
                </MenuPopover>
            </Menu>
        ) : null;

        return (
            <>
                <div
                    className={`${styles.teamListItem} ${isDisabled ? styles.disabled : ""} ${this.state.isHovered ? styles.hovered : ""}`}
                    role="listitem"
                    aria-label={`${teamInfo.teamName}. ${ariaDescription}`}
                    onMouseEnter={this.handleMouseEnter}
                    onMouseLeave={this.handleMouseLeave}
                >
                    <div className={styles.teamInfo}>
                        {this.renderTeamAvatar(teamInfo)}
                        <div className={styles.teamDetails}>
                            <div className={styles.teamName}>{teamInfo.teamName}</div>
                            <div className={styles.teamMetadata} title={metadataString}>
                                {metadataItems.map((item, index) => (
                                    <React.Fragment key={index}>
                                        {index > 0 && <span className={styles.metadataSeparator}>|</span>}
                                        <span>{item}</span>
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className={styles.teamActions}>
                        {actionButton}
                    </div>
                </div>
                {this.state.showPastSchedulesDialog && (
                    <PastSchedulesDialog
                        isOpen={this.state.showPastSchedulesDialog}
                        onClose={this.handleClosePastSchedulesDialog}
                        onRestore={this.handleRestoreSchedule}
                        teamName={teamInfo.teamName}
                        pastSchedules={this.getRestorableSchedulesForGroup()}
                    />
                )}
            </>
        );
    }
}
